import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { translate } from '@optimusgps/optimus-intl';
import { Badge, Col, message, Row } from 'antd';
import { DateType } from '@/utilities/dateUtils.type';
import {
	dateToString,
	formatDate,
	getCurrentDate,
	getJsDate,
	getUtcStartAndEndOfDate,
	stringToDayjs,
} from '../../../../utilities/dateUtils';
import Calendar from '../../../components/dayjs/Calendar';
import {
	AMERICAN_DATE_FORMAT,
	COLOR_STATUS_MAP,
	STANDARD_DATE_FORMAT,
	YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT,
} from '../../../constants';
import { StatusByDate } from '../../../interfaces';
import { TaskService } from '../../../services';
import CalendarList from './CalendarList';

const TasksCalendar = () => {
	const intl = useIntl();

	const [value, setValue] = useState<DateType>(getCurrentDate());
	const [statusByDate, setStatusByDate] = useState<StatusByDate[]>();
	const [markedDates, setMarkedDates] = useState<string[]>();

	const fetchCalendarData = async (date: DateType) => {
		try {
			const today = formatDate(AMERICAN_DATE_FORMAT, new Date());
			const { startOfDate: startOfToday } = getUtcStartAndEndOfDate(today, AMERICAN_DATE_FORMAT, 'day');

			const selectedDate = getJsDate(date);
			const { startOfDate: startOfMonth, endOfDate: endOfMonth } = getUtcStartAndEndOfDate(
				selectedDate,
				YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT,
				'month'
			);

			const startOfMonthString = dateToString(startOfMonth, YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT);
			const endOfMonthString = dateToString(endOfMonth, YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT);
			const startOfTodayString = dateToString(startOfToday, YEAR_MONTH_DAY_TIME_WITH_MILISECONDS_FORMAT);

			const response = await TaskService.statusByDate(startOfMonthString, endOfMonthString, startOfTodayString);
			if (response.success) {
				const statusByDateArray = response.success;
				setStatusByDate(statusByDateArray);
				let days: string[] = [];
				statusByDateArray.forEach((item) => {
					const dayOfTheMonth = stringToDayjs(item.dueDate).format(STANDARD_DATE_FORMAT);
					days.push(dayOfTheMonth);
				});
				setMarkedDates(days);
			} else {
				message.error(intl.formatMessage(translate('commonError')));
			}
		} catch (e) {
			message.error(intl.formatMessage(translate('commonError')));
		}
	};

	const getDateColorScheme = (date: DateType) => {
		const formatedDate = date.format(STANDARD_DATE_FORMAT);
		const dotList: Record<string, string>[] = [];

		if (markedDates?.includes(formatedDate)) {
			const dayStatus = statusByDate?.find(
				(item) => stringToDayjs(item.dueDate).format(STANDARD_DATE_FORMAT) === formatedDate
			);

			if (dayStatus?.status) {
				dayStatus?.status.forEach((status) => {
					const id = `${date.toString()} ${status}`;
					switch (status) {
						case 'UNASSIGNED':
							dotList.push({ color: COLOR_STATUS_MAP.UNASSIGNED, id });
							break;
						case 'UNCOMPLETED':
							dotList.push({ color: COLOR_STATUS_MAP.UNCOMPLETED, id });
							break;
						default:
							break;
					}
				});
			}
		}

		return dotList;
	};

	const onSelect = (newValue: DateType) => {
		setValue(newValue);
	};

	const dateCellRender = (value: DateType) => {
		const dots = getDateColorScheme(value);
		return (
			<ul className="events">
				{dots.map((item) => (
					<li key={item.id}>
						<Badge color={item.color} />
					</li>
				))}
			</ul>
		);
	};

	const onChange = (date: DateType) => {
		const monthChanges = date.month() !== value.month();

		if (monthChanges || !value) {
			fetchCalendarData(date);
			setMarkedDates([]);
		}
		setValue(date);
	};

	useEffect(() => {
		setValue(getCurrentDate());
		fetchCalendarData(getCurrentDate());
	}, []);

	return (
		<div className="task-calendar overview-card">
			<Row className="overview-title">
				<FormattedMessage {...translate('calendar')} />
			</Row>
			<Row className="">
				<Col xs={24} sm={24} md={24} lg={14} xl={14}>
					<Calendar
						mode="month"
						dateCellRender={dateCellRender}
						fullscreen={false}
						onSelect={onSelect}
						value={value}
						onChange={onChange}
					/>
				</Col>
				<Col xs={24} sm={24} md={24} lg={10} xl={10}>
					<CalendarList selectedDate={value.format(AMERICAN_DATE_FORMAT)} />
				</Col>
			</Row>
		</div>
	);
};

export default TasksCalendar;
